import { faPresentationScreen } from "@fortawesome/pro-solid-svg-icons/faPresentationScreen";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Card, CardContent, Dialog, DialogContent, Grid, IconButton, Skeleton, Typography } from '@mui/material';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { MprcContainer, MprcDialogTitleClose, MprcLink, MprcRequestErrorReport, MprcSpinnerIndicator } from 'components';
import { MprcFeedTypography } from 'components/MprcFeedTypography';
import { MprcSwitchPlayback } from 'components/MprcSwitchPlayback';
import { DevPlanActivityContentDto, FeedContentLoadStatus, FeedItemDto } from 'generated/CoreApi_v1';
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import { useModalUserProfile } from 'providers/UserProfileProvider';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardOwnPropsOptions } from 'views/knowledge/components/card';
import { BuildLigthBoxSlides, CardLightBox } from 'views/knowledge/components/card/behaviors/common/CardLightbox';
import { InternalContentMedia } from 'views/knowledge/components/card/behaviors/common/ContentMedia';
import { InternalAttach } from 'views/knowledge/components/card/behaviors/common/DefaultAttach';
import FeedItemHelper from 'views/knowledge/components/card/FeedItemHelper';
import { PrepareCardOwnProps } from "views/knowledge/components/card/PrepareCardOwnProps";

interface IProps {
    publicationId: string;
    onClose: () => void;
}

export const ActivityViewer: React.FC<IProps> = ({ publicationId, onClose }) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { showUser } = useModalUserProfile();

    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();

    const [publication, setPublication] = useState<DevPlanActivityContentDto>();
    const [cardOwnProps, setCardOwnProps] = useState<CardOwnPropsOptions>();

    const [localContent, setLocalContent] = useState<string>("");
    const [loadingContent, setLoadingContent] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(false);
    const [openLightbox, setOpenLightbox] = useState<boolean>(false);
    const feedItemHelper = useRef<FeedItemHelper | undefined>(undefined);

    const slides = useMemo(() => (
        publication && publication.presentation && publication.attachs
            ? BuildLigthBoxSlides(publication.presentation, publication.attachs, publication.presentation.rotationInterval == 0)
            : []
    ), [publication]);

    ///
    const loadContent = (publicationId: string) => {
        setLoadingContent(true);
        api.v1.knowledgePublication.getContent({ publicationId: publicationId })
            .then((result) => {
                setLocalContent(result.data);
                setExpanded(true);
                setPublication(state => ({ ...state!, contentLoadStatus: FeedContentLoadStatus.AllContentLoaded }));
            })
            .catch((e) => console.error(e))
            .finally(() => setLoadingContent(false));
    };

    ///
    const toogleStatus = () => {
        if (publication!.contentLoadStatus === FeedContentLoadStatus.AllContentLoaded) {
            setExpanded(!expanded);
        }
        else if (publication!.contentLoadStatus === FeedContentLoadStatus.LoadContentInDemand) {
            loadContent(publication!.publicationId);
        }
    };

    useEffect(() => {
        setLoading(true);
        api.v1.devPlan.getDevPlanActivityContent({ publicationId })
            .then(response => {
                feedItemHelper.current = new FeedItemHelper(response.data as unknown as FeedItemDto);
                setCardOwnProps(PrepareCardOwnProps(response.data as unknown as FeedItemDto, feedItemHelper.current));
                setPublication(response.data);
            })
            .catch(e => setViewError(e))
            .finally(() => setLoading(false));
    }, []);


    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            onClose={onClose}
        >
            <MprcDialogTitleClose title={t("DevPlan.Activity.ViewerTitle")} shrinkPadding onClose={onClose} />
            <DialogContent sx={{ p: 1.5 }}>
                {
                    loading
                        ? <Skeleton variant="rectangular" width="100%" height={"500px"} />
                        : viewError
                            ? <MprcRequestErrorReport error={viewError} />
                            : publication &&
                            <MprcContainer>
                                <Card elevation={0}>
                                    <CardContent sx={{ p: 1, pb: 0 }}>
                                        <Grid container width="100%" direction="column" gap={1}>
                                            <Grid id="mediaHeaderSection" width="100%">
                                                <InternalContentMedia
                                                    publicationId={publication.publicationId}
                                                    presentation={publication.presentation}
                                                    attachs={publication.attachs}
                                                    cardOwnProps={cardOwnProps!}
                                                    onUpdateCardOwnProps={(data: Partial<CardOwnPropsOptions>) =>
                                                        setCardOwnProps((state) => {
                                                            const newState = { ...state!, ...data };
                                                            return newState;
                                                        })
                                                    }
                                                />
                                                {
                                                    feedItemHelper.current != undefined &&
                                                    (feedItemHelper.current.hasAutoPlayPresentation || feedItemHelper.current.hasPresentation) &&
                                                    <Grid container spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
                                                        {
                                                            feedItemHelper.current.hasAutoPlayPresentation &&
                                                            <Grid>
                                                                <MprcSwitchPlayback
                                                                    checked={cardOwnProps!.presentation.autoplay}
                                                                    onChange={
                                                                        (_, checked) => setCardOwnProps({ ...cardOwnProps!, presentation: { ...cardOwnProps!.presentation, autoplay: checked } })
                                                                    }
                                                                />
                                                            </Grid>
                                                        }
                                                        {
                                                            feedItemHelper.current.hasPresentation && publication.presentation &&
                                                            <Grid>
                                                                <Typography variant="iconText">
                                                                    <IconButton color="inherit"
                                                                        onClick={() => {
                                                                            setOpenLightbox(true);
                                                                            setCardOwnProps(state => ({ ...state!, presentation: { ...state!.presentation, lighboxOpened: true } }));
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faPresentationScreen} color="inherit" />
                                                                    </IconButton>
                                                                </Typography>
                                                                <CardLightBox
                                                                    slides={slides}
                                                                    open={openLightbox}
                                                                    index={0}
                                                                    autoplay={publication.presentation.rotationInterval > 0}
                                                                    rotationInterval={publication.presentation.rotationInterval > 0 ? publication.presentation!.rotationInterval : 5}
                                                                    onClose={() => {
                                                                        setOpenLightbox(false);
                                                                        setCardOwnProps(state => ({ ...state!, presentation: { ...state!.presentation, lighboxOpened: false } }));
                                                                    }}
                                                                />
                                                            </Grid>
                                                        }
                                                    </Grid>
                                                }
                                            </Grid>
                                            <Grid width="100%">
                                                <Grid container gap={1}>
                                                    <Grid size="grow" container direction="column" gap={1} width={"100%"}>
                                                        <Grid id="titleSection" width="100%">
                                                            <MprcFeedTypography variant="title" component="div" noWrap color="black">
                                                                <span title={publication.title}>{publication.title}</span>
                                                            </MprcFeedTypography>
                                                        </Grid>
                                                        <Grid width="100%">
                                                            <Grid container justifyContent="center" alignItems="center" spacing={1} wrap="nowrap">
                                                                <Grid>
                                                                    <Avatar
                                                                        className={AppGlobalClassKeys.clickable}
                                                                        src={publication.author.thumbnailUrl}
                                                                        onClick={() => showUser(publication.author.userId)}
                                                                    />

                                                                </Grid>
                                                                <Grid size="grow">
                                                                    <MprcLink onClick={() => showUser(publication.author.userId)} variant="body2" fontWeight="bold">
                                                                        <Typography variant="body2" fontWeight="bold" noWrap>
                                                                            {publication.author.name}
                                                                        </Typography>
                                                                    </MprcLink>
                                                                    {publication.publishedOn &&
                                                                        <MprcFeedTypography variant="authorSecondary" component="div">
                                                                            <span>{t("knowledge:Feed.Card.Common.PublishOnText")} {Formaters.relativeTime(publication.publishedOn)}</span>
                                                                            {publication.editedOn && <span> {t("knowledge:Feed.Card.Common.EditedText", { date: Formaters.relativeTime(publication.editedOn) })}</span>}
                                                                        </MprcFeedTypography>
                                                                    }
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid width="100%">
                                                            <Grid container gap={1} justifyContent="center">
                                                                <Grid id="contentSection" className={AppGlobalClassKeys.qlContentSection} width="100%">
                                                                    <MprcFeedTypography variant="content" component="div">
                                                                        <div dangerouslySetInnerHTML={{
                                                                            __html: !expanded || publication.contentLoadStatus == FeedContentLoadStatus.NoActionNecessary
                                                                                ? publication.content ?? ""
                                                                                : localContent
                                                                        }}></div>
                                                                    </MprcFeedTypography>
                                                                    {
                                                                        publication.contentLoadStatus !== FeedContentLoadStatus.NoActionNecessary &&
                                                                        <>
                                                                            {
                                                                                loadingContent
                                                                                    ? <MprcSpinnerIndicator />
                                                                                    : <MprcFeedTypography variant="moreContent" component="a" onClick={() => toogleStatus()}>
                                                                                        {expanded ? t("knowledge:Feed.Card.Common.SeeLessText") : t("knowledge:Feed.Card.Common.SeeMoreText")}
                                                                                    </MprcFeedTypography>
                                                                            }
                                                                        </>
                                                                    }
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid id="attachSection" width="100%">
                                                <InternalAttach
                                                    publicationId={publication.publicationId}
                                                    attachs={publication.attachs}
                                                    helper={feedItemHelper.current!}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </MprcContainer>
                }


            </DialogContent>
        </Dialog >
    );
};
